import type { JSX, MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { History } from 'lucide-react';

import { formatTimeAgo } from '~/utils/formatTimestamp';
import { logger } from '~/utils/logger';
import { getProjectStatusIcon } from '~/utils/projectStatus';

import { ConfirmationDialog } from '~/components/ConfirmationDialog';
import { Tooltip } from '~/components/Tooltip';
import { JiraIcon } from '~/pages/Jira/JiraIcon';

import type { ProjectProps, ProjectStatus } from '~/types';

import { JobHistoryModal } from './JobHistoryModal';
import { ProjectCardActions } from './ProjectCardActions';
import { ProjectCardPRDPanel } from './ProjectCardPRDPanel';
import { ProjectStatusSelect } from './ProjectStatusSelect';
import { TaskProgressBar } from './TaskProgressBar';

interface ProjectCardProps {
  project: ProjectProps;
  onSelect: (projectId: string) => void;
  onStatusChange: (projectId: string, status: ProjectProps['status']) => void;
  onUpdate: (projectId: string, updates: Partial<ProjectProps>) => void;
  onDelete: (projectId: string) => void;
  onGenerateTasksFromPRD: (projectId: string, prdContent: string) => Promise<void>;
  isSelected: boolean;
  /** 'row' (default) is the compact sidebar-list presentation (List.tsx); 'board'
   *  is the Kanban board card (ProductView.tsx) — same bg-surface-elevated token
   *  TaskCard uses, so board cards read as one visual family across the app. */
  variant?: 'row' | 'board';
}

export function ProjectCard(props: ProjectCardProps): JSX.Element {
  const {
    isSelected,
    onDelete,
    onGenerateTasksFromPRD,
    onSelect,
    onStatusChange,
    project,
    variant = 'row',
  } = props;

  const cardRef = useRef<HTMLDivElement>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showJobHistory, setShowJobHistory] = useState(false);

  // Compute the most recent update: project or PRD
  const mostRecentUpdate = useMemo(() => {
    if (!project.prdUpdatedAt) {
      return project.updatedAt;
    }
    const projectTime = new Date(project.updatedAt).getTime();
    const prdTime = new Date(project.prdUpdatedAt).getTime();
    return prdTime > projectTime ? project.prdUpdatedAt : project.updatedAt;
  }, [project.updatedAt, project.prdUpdatedAt]);

  const handleDelete = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const confirmDelete = useCallback(() => {
    onDelete(project.id);
    setShowDeleteConfirm(false);
  }, [onDelete, project.id]);

  const cancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  const handleProjectSelect = useCallback(() => {
    onSelect(project.id);
  }, [onSelect, project.id]);

  const handleStatusChange = useCallback(
    (status: ProjectStatus) => {
      onStatusChange(project.id, status);
    },
    [onStatusChange, project.id],
  );

  const stopPropagation = useCallback((e: MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleOpenJobHistory = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      logger.info(
        { component: 'ProjectCard', projectId: project.id },
        'User opened job history modal',
      );
      setShowJobHistory(true);
    },
    [project.id],
  );

  // Scroll into view when project becomes selected
  useEffect(() => {
    if (isSelected && cardRef.current) {
      cardRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [isSelected]);

  return (
    <>
      {/** biome-ignore lint/a11y/useSemanticElements: <explanation> */}
      <div
        ref={cardRef}
        aria-label={`Project: ${project.title}`}
        aria-selected={isSelected}
        className={clsx(
          'group px-3 py-2.5 transition-colors cursor-pointer focus:outline-none',
          variant === 'board'
            ? 'bg-surface-elevated dark:bg-surface-elevated border border-default shadow-sm hover:shadow-md hover:border-primary dark:hover:border-primary'
            : clsx(
                'bg-foreground/[0.06] dark:bg-white/[0.08]',
                isSelected
                  ? 'bg-foreground/[0.10] dark:bg-white/[0.14]'
                  : 'hover:bg-foreground/[0.10] dark:hover:bg-white/[0.12]',
              ),
          isSelected ? 'ml-1 border-r-3 border-r-warning rounded-lg' : 'ml-1 rounded-lg',
        )}
        onClick={handleProjectSelect}
        onKeyDown={e => {
          // Don't handle keyboard events if focus is on an input element
          const target = e.target as HTMLElement;
          if (
            target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.tagName === 'SELECT'
          ) {
            return;
          }

          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleProjectSelect();
          }
        }}
        tabIndex={0}
        role="option"
      >
        {/* Line 1: [StatusIcon] [Title] */}
        <div className="flex items-center gap-2 mb-0.5">
          {getProjectStatusIcon(project.status)}
          <span className="text-foreground text-sm line-clamp-1 flex-1">{project.title}</span>
        </div>

        {/* Line 2: [Status badge] [Jira] [time ago] [flex-1] [Owner] [Actions] */}
        <div className="flex items-center gap-1.5 text-xs">
          <ProjectStatusSelect
            status={project.status}
            onStatusChange={handleStatusChange}
            onClick={stopPropagation}
            aria-label={`Status for ${project.title}`}
          />
          {project.jiraSourceKey && (
            <>
              <span className="text-foreground-500">&middot;</span>
              <span className="flex items-center gap-0.5 text-foreground-600">
                <JiraIcon className="w-3 h-3" />
                {project.jiraSourceKey}
              </span>
            </>
          )}
          <span className="text-foreground-500">&middot;</span>
          <span className="text-foreground-600">{formatTimeAgo(mostRecentUpdate)}</span>
          <div className="flex-1" />
          {project.creatorDisplayName && (
            <span className="shrink-0 px-1.5 py-0.5 rounded bg-primary-100 dark:bg-primary-200 text-primary-700 dark:text-primary-800 text-xs font-medium">
              {project.creatorDisplayName}
            </span>
          )}
          <ProjectCardActions
            projectId={project.id}
            projectTitle={project.title}
            onClickDelete={handleDelete}
            stopPropagation={stopPropagation}
          />
        </div>

        {/* Task progress bar — always shown so every card carries a
            consistent bottom row, even at zero tasks (0/0, 0%). The job-history
            button keeps its original visibility (tasks exist, or project done). */}
        <div className="flex items-center gap-1 mt-1">
          <div className="flex-1">
            <TaskProgressBar
              completedTasks={project.completedTasks ?? 0}
              totalTasks={project.totalTasks ?? 0}
            />
          </div>
          {((project.totalTasks ?? 0) > 0 || project.status === 'DONE') && (
            <Tooltip content="View job history">
              <button
                type="button"
                aria-label="View job history"
                onClick={handleOpenJobHistory}
                className="inline-flex items-center justify-center p-1 rounded text-foreground-500 hover:text-foreground hover:bg-foreground-100 transition-colors shrink-0"
              >
                <History className="w-3.5 h-3.5" />
              </button>
            </Tooltip>
          )}
        </div>

        <ProjectCardPRDPanel project={project} onGenerateTasksFromPRD={onGenerateTasksFromPRD} />
      </div>

      {/* Job History Modal */}
      <JobHistoryModal
        projectId={project.id}
        isOpen={showJobHistory}
        onClose={() => setShowJobHistory(false)}
        hasTasks={(project.totalTasks ?? 0) > 0}
        totalTasks={project.totalTasks ?? 0}
        completedTasks={project.completedTasks ?? 0}
      />

      {showDeleteConfirm && (
        <ConfirmationDialog
          content={
            <>
              <p>Are you sure you want to delete {project.title}?</p>
              <p>This will also delete all its tasks and cannot be undone.</p>
            </>
          }
          isOpen={showDeleteConfirm}
          onConfirm={confirmDelete}
          onClose={cancelDelete}
          title="Delete Project"
        />
      )}
    </>
  );
}
